import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Trophy, ArrowRight } from "lucide-react";
import { compareChannels } from "@/lib/api";
import { formatCompact, formatUSD } from "@/lib/format";
import LoadingSequence from "@/components/LoadingSequence";
import SEO from "@/components/SEO";
import ChannelSearchInput, { isDirectChannelRef } from "@/components/ChannelSearchInput";

const ROWS = [
  { key: "subscribers", label: "Subscribers", get: (s) => s.subscribers, fmt: formatCompact },
  { key: "total_views", label: "Total views", get: (s) => s.total_views, fmt: formatCompact },
  { key: "video_count", label: "Videos", get: (s) => s.video_count, fmt: formatCompact },
  { key: "avg_views", label: "Avg. views", get: (s) => s.avg_views, fmt: formatCompact },
  { key: "avg_likes", label: "Avg. likes", get: (s) => s.avg_likes, fmt: formatCompact },
  { key: "avg_comments", label: "Avg. comments", get: (s) => s.avg_comments, fmt: formatCompact },
  { key: "engagement_rate", label: "Engagement", get: (s) => `${s.engagement_rate}%` },
];

const Side = ({ data, side, comparison }) => {
  if (!data) return null;
  const { stats, score, revenue } = data;
  return (
    <div data-testid={`compare-side-${side}`} className="card-flat p-6">
      <div className="flex items-center gap-3">
        <img src={stats.thumbnail} alt={`${stats.title} channel avatar`} loading="lazy" className="w-14 h-14 rounded-lg border border-border" />
        <div>
          <div className="font-bold text-lg">{stats.title}</div>
          <div className="text-xs text-muted-foreground font-mono">{stats.country || "—"}</div>
        </div>
      </div>
      <div className="mt-6 space-y-2">
        {ROWS.map((r) => {
          const w = comparison[r.key];
          const isWinner = w === side;
          return (
            <div key={r.key} className={`flex items-center justify-between px-3 py-2 rounded-md ${isWinner ? "bg-primary/10 border border-primary/30" : "bg-secondary/40"}`}>
              <span className="text-sm text-muted-foreground">{r.label}</span>
              <span className={`font-mono tabular-nums font-bold ${isWinner ? "text-primary" : ""}`}>
                {r.fmt ? r.fmt(r.get(stats)) : r.get(stats)}
                {isWinner && <Trophy className="inline w-3.5 h-3.5 ml-1.5" />}
              </span>
            </div>
          );
        })}
        <div className={`flex items-center justify-between px-3 py-2 rounded-md ${comparison.score === side ? "bg-primary/10 border border-primary/30" : "bg-secondary/40"}`}>
          <span className="text-sm text-muted-foreground">Score</span>
          <span className={`font-mono tabular-nums font-bold text-lg ${comparison.score === side ? "text-primary" : ""}`}>
            {score.overall}/100
            {comparison.score === side && <Trophy className="inline w-3.5 h-3.5 ml-1.5" />}
          </span>
        </div>
        <div className={`flex items-center justify-between px-3 py-2 rounded-md ${comparison.revenue === side ? "bg-primary/10 border border-primary/30" : "bg-secondary/40"}`}>
          <span className="text-sm text-muted-foreground">Estimated monthly revenue</span>
          <span className={`font-mono tabular-nums font-bold ${comparison.revenue === side ? "text-primary" : ""}`}>
            {formatUSD(revenue.mid.monthly)}
            {comparison.revenue === side && <Trophy className="inline w-3.5 h-3.5 ml-1.5" />}
          </span>
        </div>
      </div>
    </div>
  );
};

export default function Compare() {
  // Free-text query the user typed for each side.
  const [aQuery, setAQuery] = useState("");
  const [bQuery, setBQuery] = useState("");
  // Resolved reference (URL) chosen from the picker for each side.
  const [aRef, setARef] = useState("");
  const [bRef, setBRef] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  // Any typed change invalidates the previously picked ref, so the user
  // can't accidentally submit a stale selection after editing the input.
  const onChangeA = (v) => { setAQuery(v); setARef(""); };
  const onChangeB = (v) => { setBQuery(v); setBRef(""); };

  // resolveRef returns the value we should send to the backend for a side.
  // Preference order: an explicit pick from the dropdown, then a directly
  // typed URL/handle/ID (which the backend can resolve on its own).
  const resolveRef = (query, ref) => {
    if (ref) return ref;
    if (isDirectChannelRef(query)) return query.trim();
    return null;
  };

  const submit = async (e) => {
    e.preventDefault();
    const refA = resolveRef(aQuery, aRef);
    const refB = resolveRef(bQuery, bRef);
    if (!refA || !refB) {
      toast.error("Pick both channels from the dropdown, or paste their URLs");
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const r = await compareChannels(refA, refB);
      setResult(r);
      toast.success("Comparison complete");
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || "Comparison failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-20">
      <SEO
        title="Compare 2 YouTube Channels Side-by-Side (Free Tool, 30s)"
        description="Compare any two YouTube channels head-to-head: subscribers, views, engagement rate, revenue estimate, upload frequency and AI score. Search by name or paste a URL."
        path="/compare"
        keywords="compare youtube channels, youtube channel comparator, search youtube channel by name, youtube analytics tool, channel growth analysis"
      />
      <h1 className="text-4xl font-black tracking-tight">Compare Channels</h1>
      <p className="mt-3 text-muted-foreground">
        Search by <strong className="text-foreground">channel name</strong> or paste a URL / @handle. Pick two channels and we'll show who wins on every metric.
      </p>

      <form onSubmit={submit} className="mt-8 grid md:grid-cols-[1fr_auto_1fr_auto] gap-3 items-start">
        <div>
          <label className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground pl-1">Channel A</label>
          <div className="mt-1">
            <ChannelSearchInput
              value={aQuery}
              onChange={onChangeA}
              onResolve={(url) => setARef(url)}
              placeholder="Type a name or paste a URL"
              testId="compare-input-a"
            />
          </div>
        </div>
        <div className="hidden md:block pt-8 text-xs font-mono text-muted-foreground text-center">VS</div>
        <div>
          <label className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground pl-1">Channel B</label>
          <div className="mt-1">
            <ChannelSearchInput
              value={bQuery}
              onChange={onChangeB}
              onResolve={(url) => setBRef(url)}
              placeholder="Type a name or paste a URL"
              testId="compare-input-b"
            />
          </div>
        </div>
        <Button
          data-testid="compare-submit"
          type="submit"
          className="h-12 rounded-xl bg-primary hover:bg-primary/90 px-6 font-bold md:mt-8"
        >
          Compare <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </form>

      {loading && <LoadingSequence />}

      {result && !loading && (
        <div className="mt-12 grid md:grid-cols-2 gap-4">
          <Side data={result.a} side="a" comparison={result.comparison} />
          <Side data={result.b} side="b" comparison={result.comparison} />
        </div>
      )}
    </div>
  );
}
